import React, { useRef, useEffect, useCallback } from 'react'
import { clamp, createLinearScale, calculateWeightedAverage } from '@utils/math'
import useAnimationFrame from '@hooks/useAnimationFrame'
import { startFreeScroll, cancelFreeScroll } from '../actions'
import type { ScrollerData, Position } from '../types/types'
import { createLogger } from '@utils/logger'
import type { Action } from '../reducers'

const freescrollLog = createLogger('FreescrollHook')
freescrollLog.setLevel('info')

const FRICTION = 0.95
const VELOCITY_CALCULATION_CUTOFF_MS = 200 // Time window for calculating velocity
const MIN_VELOCITY_THRESHOLD = 0.01 // Velocity below which animation stops
const MAX_DELTA_TIME_MS = 1000 // Max delta time to prevent huge jumps if tab is backgrounded
const MAX_TRACKED_POSITIONS = 100

interface TimestampedPosition extends Position {
  timeStamp: number
}

interface UseFreescrollProps {
  scrollerData: ScrollerData
  onPanMove: (nextLeft: number, nextRight: number) => void
  dispatch: React.Dispatch<Action>
  freeScrolling: boolean
}

interface UseFreescrollReturn {
  handleFreescrollPanStart: () => void
  handleFreescrollPanMove: (center: Position, timeStamp: number) => void
  handleFreescrollPanEnd: () => void
  cancelFreescrollAnimation: () => void
  timelineDrag: (movementX: number) => void
}

const useFreescroll = ({
  scrollerData,
  onPanMove,
  dispatch,
  freeScrolling,
}: UseFreescrollProps): UseFreescrollReturn => {
  const prevPositionRef = useRef<Position | null>(null)
  const velocityXRef = useRef<number>(0)
  const lastAnimationTimestampRef = useRef<number | null>(null)
  const positionsRef = useRef<TimestampedPosition[]>([])
  const scrollerDataRef = useRef(scrollerData)

  useEffect(() => {
    scrollerDataRef.current = scrollerData
  }, [scrollerData])

  const resetAnimationVariables = useCallback(() => {
    prevPositionRef.current = null
    positionsRef.current = []
    lastAnimationTimestampRef.current = null
    // velocityXRef.current is intentionally not reset here as it might be needed immediately after a pan end
  }, [])

  const cancelFreescrollAnimation = useCallback(() => {
    dispatch(cancelFreeScroll())
    lastAnimationTimestampRef.current = null // Ensure animation stops
  }, [dispatch])

  const nextAnimationFrameHandler = useCallback(() => {
    const lastTimestamp = lastAnimationTimestampRef.current
    const currentTimestamp = performance.now()

    if (lastTimestamp && freeScrolling) {
      let deltaTime = clamp(currentTimestamp - lastTimestamp, 0, MAX_DELTA_TIME_MS)
      velocityXRef.current *= Math.pow(FRICTION, deltaTime / 16) // Apply friction adjusted for deltaTime (assuming 60fps baseline)

      const absoluteVelocity = Math.abs(velocityXRef.current)
      if (absoluteVelocity < MIN_VELOCITY_THRESHOLD) {
        lastAnimationTimestampRef.current = null
        cancelFreescrollAnimation()
      } else {
        const movementX = velocityXRef.current * deltaTime
        const currentScrollerData = scrollerDataRef.current
        const surfaceX = currentScrollerData.surface.x
        const targetSurfaceX = surfaceX + movementX

        const surfaceWidth = currentScrollerData.surface.width
        const viewportWidth = currentScrollerData.viewport.width

        let finalSurfaceX: number

        if (surfaceWidth > viewportWidth) {
          // Content is scrollable, apply freescroll with bounce logic
          const minScrollableX = viewportWidth - surfaceWidth // Negative value, e.g., 800 - 1000 = -200
          const maxScrollableX = 0 // Rightmost position for surface.x

          const movingLeft = velocityXRef.current < 0
          const movingRight = velocityXRef.current > 0

          // Check if the target position crosses a boundary while moving towards it
          const hitLeftBoundary = targetSurfaceX < minScrollableX && movingLeft
          const hitRightBoundary = targetSurfaceX > maxScrollableX && movingRight

          if (hitLeftBoundary) {
            finalSurfaceX = minScrollableX
            velocityXRef.current *= -0.4 // Bounce with damping
            // If velocity is very small after bounce, set to 0 to ensure it stops soon.
            if (Math.abs(velocityXRef.current) < MIN_VELOCITY_THRESHOLD / 2) {
              velocityXRef.current = 0
            }
            freescrollLog.debug('Bounced off left boundary', { newVelocity: velocityXRef.current })
          } else if (hitRightBoundary) {
            finalSurfaceX = maxScrollableX
            velocityXRef.current *= -0.4 // Bounce with damping
            if (Math.abs(velocityXRef.current) < MIN_VELOCITY_THRESHOLD / 2) {
              velocityXRef.current = 0
            }
            freescrollLog.debug('Bounced off right boundary', { newVelocity: velocityXRef.current })
          } else {
            // No bounce: clamp the position to the scrollable range.
            finalSurfaceX = clamp(targetSurfaceX, minScrollableX, maxScrollableX)
            // If clamping stopped it exactly at a boundary and it was still moving towards it,
            // set velocity to 0 to prevent "sticking".
            if ((finalSurfaceX === minScrollableX && movingLeft) || (finalSurfaceX === maxScrollableX && movingRight)) {
              velocityXRef.current = 0
            }
          }
        } else {
          // Content is not wider than viewport, so it shouldn't scroll.
          finalSurfaceX = 0 // Pin to the start.
          velocityXRef.current = 0 // Stop any residual velocity.
        }

        const newViewportLeft = Math.abs(finalSurfaceX)
        const newViewportRight = newViewportLeft + viewportWidth // Use viewportWidth consistent with above

        const scale = createLinearScale([0, surfaceWidth], [0, 1]) // Use surfaceWidth consistent with above
        const nextLeft = scale(newViewportLeft)
        const nextRight = scale(newViewportRight)

        onPanMove(nextLeft, nextRight)
        lastAnimationTimestampRef.current = currentTimestamp
      }
    } else if (freeScrolling) {
      // Start of animation or recovering from lost timestamp
      lastAnimationTimestampRef.current = currentTimestamp
    } else {
      lastAnimationTimestampRef.current = null // Ensure if freeScrolling becomes false, we stop.
    }
  }, [freeScrolling, onPanMove, cancelFreescrollAnimation])

  useAnimationFrame(nextAnimationFrameHandler, freeScrolling)

  const trackPosition = useCallback((x: number, y: number, timeStamp: number) => {
    const existingPositions = positionsRef.current
    let updatedPositions: TimestampedPosition[] =
      existingPositions.length >= MAX_TRACKED_POSITIONS
        ? existingPositions.slice(existingPositions.length - MAX_TRACKED_POSITIONS + 1)
        : existingPositions.slice()
    updatedPositions.push({ x, y, timeStamp })
    positionsRef.current = updatedPositions
  }, [])

  const calculateVelocity = useCallback(() => {
    const cutoff = performance.now() - VELOCITY_CALCULATION_CUTOFF_MS
    const recentPositions = positionsRef.current.filter(p => p.timeStamp > cutoff)

    if (recentPositions.length < 2) {
      freescrollLog.debug('Not enough recent positions to calculate real velocity')
      velocityXRef.current = 0
    } else {
      let velocities = recentPositions.slice(1).reduce((acc: number[], position: TimestampedPosition, i: number) => {
        const prevPosition = recentPositions[i] // Correctly refers to the element before current `position` in `recentPositions`
        const elapsedMS = position.timeStamp - prevPosition.timeStamp
        if (elapsedMS > 0) {
          const velocity = (position.x - prevPosition.x) / elapsedMS
          acc.push(velocity)
        }
        return acc
      }, [])

      if (velocities.length > 0) {
        // Reverse velocities to give more weight to the most recent ones
        // calculateWeightedAverage gives more weight to earlier elements in the array.
        velocities.reverse()
        velocityXRef.current = calculateWeightedAverage(velocities, 2) // Growth factor can be tuned.
        freescrollLog.debug('Calculated velocity X:', velocityXRef.current, 'from velocities:', velocities)
      } else {
        freescrollLog.debug('No valid velocities calculated.')
        velocityXRef.current = 0
      }
    }
  }, [freescrollLog])

  const handleFreescrollPanStart = useCallback(() => {
    cancelFreescrollAnimation()
    resetAnimationVariables()
    // prevPositionRef is set by the component calling this, if needed for direct drag.
    // For freescroll, we only care about the state *before* pan ends.
  }, [cancelFreescrollAnimation, resetAnimationVariables])

  const handleFreescrollPanMove = useCallback(
    (center: Position, timeStamp: number) => {
      trackPosition(center.x, center.y, timeStamp)
      // The actual timelineDrag logic will be called by the component
      // This hook's role during pan move is primarily to track positions for velocity calculation
    },
    [trackPosition]
  )

  const handleFreescrollPanEnd = useCallback(() => {
    calculateVelocity() // Calculate the final velocity based on tracked pointer movements.

    // If the absolute calculated velocity is significant enough, start freescrolling.
    if (Math.abs(velocityXRef.current) > MIN_VELOCITY_THRESHOLD) {
      dispatch(startFreeScroll()) // Signal that freescrolling should begin.
      lastAnimationTimestampRef.current = performance.now() // Record the start time for the animation loop.
    } else {
      // If velocity is too low, ensure freescrolling is not initiated.
      velocityXRef.current = 0 // Explicitly set velocity to zero.
      cancelFreescrollAnimation() // Ensure any pending animation is cancelled and state is updated.
    }
  }, [dispatch, calculateVelocity, cancelFreescrollAnimation])

  const timelineDrag = useCallback(
    (movementX: number) => {
      const currentScrollerData = scrollerDataRef.current
      const viewportWidth = currentScrollerData.viewport.width
      const surfaceX = currentScrollerData.surface.x
      const surfaceWidth = currentScrollerData.surface.width

      const minX = 0 + viewportWidth - surfaceWidth
      const maxX = 0
      const nextSurfaceX = clamp(surfaceX + movementX, minX, maxX)

      const newViewportLeft = Math.abs(nextSurfaceX)
      const newViewportRight = newViewportLeft + viewportWidth

      const scale = createLinearScale([0, surfaceWidth], [0, 1])
      const nextLeft = scale(newViewportLeft)
      const nextRight = scale(newViewportRight)
      onPanMove(nextLeft, nextRight)
    },
    [onPanMove]
  )

  return {
    handleFreescrollPanStart,
    handleFreescrollPanMove,
    handleFreescrollPanEnd,
    cancelFreescrollAnimation,
    timelineDrag,
  }
}

export default useFreescroll
